import React from 'react';
import { CheckCircle2, Clock, Lock } from 'lucide-react';
import { ageBandLabel, normalizeAgeRange } from '../../utils/ageBand';

const cardStyle = {
  borderRadius: 18,
  padding: 20,
  background: 'var(--color-bg-card)',
  border: '1px solid var(--color-border)',
  boxShadow: '0 4px 18px var(--color-shadow)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  textAlign: 'left',
  minHeight: 220,
  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
};

const titleStyle = {
  margin: 0,
  fontSize: '1.2rem',
  fontWeight: 800,
  color: 'var(--color-text)',
  letterSpacing: '-0.02em',
};

const blurbStyle = {
  margin: '8px 0 0',
  fontSize: 14,
  lineHeight: 1.5,
  color: 'var(--color-text)',
  opacity: 0.76,
  flex: 1,
};

const rowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  alignItems: 'center',
  marginTop: 14,
};

const difficultyBase = {
  fontSize: 12,
  fontWeight: 700,
  padding: '4px 10px',
  borderRadius: 8,
  border: '1px solid var(--color-border)',
};

function difficultyStyleFor(level) {
  if (level === 'Beginner') {
    return {
      ...difficultyBase,
      background: 'rgba(34, 197, 94, 0.12)',
      color: 'var(--color-text)',
    };
  }
  if (level === 'Intermediate') {
    return {
      ...difficultyBase,
      background: 'rgba(99, 102, 241, 0.12)',
      color: 'var(--color-text)',
    };
  }
  return { ...difficultyBase, background: 'var(--color-bg)', color: 'var(--color-text)' };
}

const durationStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--color-text)',
  opacity: 0.7,
};

const progressLabelStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--color-text)',
  opacity: 0.5,
  marginTop: 12,
  marginBottom: 6,
};

const progressTrackStyle = {
  height: 10,
  borderRadius: 999,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
  marginBottom: 14,
};

const buttonStyle = {
  width: '100%',
  marginTop: 'auto',
  padding: '14px 16px',
  minHeight: 48,
  borderRadius: 14,
  border: 'none',
  fontWeight: 700,
  fontSize: 15,
  cursor: 'pointer',
  background: 'var(--color-primary)',
  color: '#fff',
  boxShadow: '0 2px 10px var(--color-shadow)',
};

const completedBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--color-primary)',
  marginBottom: 10,
};

const titleRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

const lockRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  fontWeight: 700,
  color: 'var(--color-text)',
  marginTop: 14,
  marginBottom: 8,
};

const parentCopyStyle = {
  fontSize: 13,
  lineHeight: 1.45,
  color: 'var(--color-text)',
  opacity: 0.72,
  margin: '0 0 14px',
};

const lockedCardStyle = {
  opacity: 0.92,
};

const lessonTitleListStyle = {
  margin: '8px 0 0',
  paddingLeft: 18,
  fontSize: 13,
  lineHeight: 1.45,
  color: 'var(--color-text)',
  opacity: 0.72,
};

function LessonMasteryDots({ statuses }) {
  if (!Array.isArray(statuses) || statuses.length === 0) return null;
  return (
    <div
      role="list"
      aria-label="Lesson progress"
      style={{
        display: 'flex',
        gap: 6,
        marginTop: 10,
        marginBottom: 4,
        flexWrap: 'wrap',
      }}
    >
      {statuses.map((s, i) => {
        const completed = !!s?.completed;
        const started = !!s?.started;
        const mastered = (s?.reviewCount || 0) >= 2;
        const baseSize = mastered ? 16 : 14;
        const bg = completed
          ? 'var(--color-primary)'
          : started
            ? 'rgba(99,102,241,0.35)'
            : 'transparent';
        const border = completed
          ? 'var(--color-primary)'
          : started
            ? 'var(--color-primary)'
            : 'var(--color-border)';
        const status = mastered
          ? 'mastered'
          : completed
            ? 'completed'
            : started
              ? 'started'
              : 'not started';
        const name = s?.lessonTitle || `Lesson ${i + 1}`;
        const label = `${name}: ${status}`;
        return (
          <span
            key={s?.lessonId || i}
            role="listitem"
            aria-label={label}
            title={label}
            style={{
              width: baseSize,
              height: baseSize,
              borderRadius: '50%',
              background: bg,
              border: `2px solid ${border}`,
              boxShadow: mastered ? '0 0 0 2px rgba(34,197,94,0.45)' : 'none',
              display: 'inline-block',
            }}
          />
        );
      })}
    </div>
  );
}

function PathLessonTitleList({ statuses }) {
  if (!Array.isArray(statuses) || statuses.length === 0) return null;
  const hasTitles = statuses.some((s) => s?.lessonTitle);
  if (!hasTitles) return null;
  return (
    <ol style={lessonTitleListStyle}>
      {statuses.map((s, i) => (
        <li key={s?.lessonId || i}>{s.lessonTitle || `Lesson ${i + 1}`}</li>
      ))}
    </ol>
  );
}

function LearningPathCard({
  path,
  onStartAdventure,
  onUnlockPremium = null,
  disabled,
  locked = false,
  completed = false,
  parentPremiumMessage = '',
  premiumCtaLabel = 'Unlock with Explore Premium',
  activeAgeBand = null,
  lessonCount = null,
  lessonStatuses = null,
  prereqLocked = false,
  prereqMessage = '',
  prereqWhyMessage = '',
  reviewDue = false,
}) {
  const pct = locked ? 0 : completed ? 100 : Math.min(100, Math.max(0, path.progress ?? 0));
  const pathBand = normalizeAgeRange(path.ageBand);
  const matchesActive = activeAgeBand && pathBand && pathBand === activeAgeBand;
  const ageBadgeLabel = pathBand ? ageBandLabel(pathBand) : '';
  const ageBadgeStyle = {
    ...difficultyBase,
    background: matchesActive ? 'rgba(34, 197, 94, 0.16)' : 'var(--color-bg)',
    color: 'var(--color-text)',
    opacity: matchesActive || !activeAgeBand ? 1 : 0.7,
  };
  const showPrereqLock = !locked && prereqLocked;

  return (
    <article
      style={locked || showPrereqLock ? { ...cardStyle, ...lockedCardStyle } : cardStyle}
      className="learning-path-card"
      aria-label={locked ? `${path.title}, locked lesson` : path.title}
      onMouseEnter={(e) => {
        if (locked) return;
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 10px 26px var(--color-shadow)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 18px var(--color-shadow)';
      }}
    >
      {completed && !locked ? (
        <div style={completedBadgeStyle}>
          <CheckCircle2 size={18} strokeWidth={2.4} aria-hidden />
          Completed
        </div>
      ) : null}
      <div style={titleRowStyle}>
        {locked || showPrereqLock ? (
          <Lock size={22} strokeWidth={2.2} aria-hidden style={{ flexShrink: 0, marginTop: 2, opacity: 0.85 }} />
        ) : null}
        <h2 style={{ ...titleStyle, flex: 1 }}>{path.title}</h2>
      </div>
      <p style={blurbStyle}>{path.blurb}</p>
      <div style={rowStyle}>
        <span style={difficultyStyleFor(path.difficulty)}>{path.difficulty}</span>
        {ageBadgeLabel ? (
          <span style={ageBadgeStyle} aria-label={`Recommended ${ageBadgeLabel}`}>
            {ageBadgeLabel}
          </span>
        ) : null}
        <span style={durationStyle}>
          <Clock size={16} strokeWidth={2.2} aria-hidden />
          {path.durationMinutes} min
        </span>
        {typeof lessonCount === 'number' && lessonCount > 0 ? (
          <span style={durationStyle} aria-label={`${lessonCount} lesson${lessonCount === 1 ? '' : 's'} in this path`}>
            {lessonCount} lesson{lessonCount === 1 ? '' : 's'}
          </span>
        ) : null}
        {reviewDue && !locked && !showPrereqLock ? (
          <span
            style={{
              ...difficultyBase,
              background: 'rgba(245, 158, 11, 0.16)',
              color: 'var(--color-text)',
            }}
            aria-label="Time for a quick review"
          >
            Review ready
          </span>
        ) : null}
      </div>
      {!locked ? (
        <>
          <LessonMasteryDots statuses={lessonStatuses} />
          <PathLessonTitleList statuses={lessonStatuses} />
        </>
      ) : null}
      {locked ? (
        <>
          <div style={lockRowStyle}>
            <Lock size={18} strokeWidth={2.2} aria-hidden />
            {premiumCtaLabel}
          </div>
          {parentPremiumMessage ? <p style={parentCopyStyle}>{parentPremiumMessage}</p> : null}
          <button
            type="button"
            style={{
              ...buttonStyle,
              opacity: onUnlockPremium ? 1 : 0.5,
              cursor: onUnlockPremium ? 'pointer' : 'not-allowed',
              background: onUnlockPremium ? 'var(--color-primary)' : 'var(--color-border)',
              color: onUnlockPremium ? '#fff' : 'var(--color-text)',
              boxShadow: onUnlockPremium ? '0 2px 10px var(--color-shadow)' : 'none',
            }}
            disabled={!onUnlockPremium}
            onClick={onUnlockPremium ? () => onUnlockPremium(path) : undefined}
            aria-label={onUnlockPremium ? `${premiumCtaLabel} (test mode)` : premiumCtaLabel}
          >
            {premiumCtaLabel}
          </button>
        </>
      ) : showPrereqLock ? (
        <>
          <div style={lockRowStyle}>
            <Lock size={18} strokeWidth={2.2} aria-hidden />
            {prereqMessage || 'Finish the previous skill first'}
          </div>
          <p style={parentCopyStyle}>
            {prereqWhyMessage ||
              'Lessons build on each other so concepts stick. Finishing the earlier skill keeps things calm and confident — no rush.'}
          </p>
          <button
            type="button"
            style={{
              ...buttonStyle,
              opacity: 0.5,
              cursor: 'not-allowed',
              background: 'var(--color-border)',
              color: 'var(--color-text)',
              boxShadow: 'none',
            }}
            disabled
            aria-label={prereqMessage || 'Locked until prerequisite is complete'}
          >
            Locked
          </button>
        </>
      ) : (
        <>
          <div style={progressLabelStyle}>Path progress</div>
          <div
            style={progressTrackStyle}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                borderRadius: 999,
                background: 'var(--color-primary)',
                opacity: 0.85,
              }}
            />
          </div>
          <button
            type="button"
            style={{
              ...buttonStyle,
              opacity: disabled ? 0.55 : 1,
              cursor: disabled ? 'wait' : 'pointer',
            }}
            onClick={() => onStartAdventure(path)}
            disabled={disabled}
          >
            {reviewDue ? 'Review now' : completed ? 'Play again' : 'Start Adventure'}
          </button>
        </>
      )}
    </article>
  );
}

export default LearningPathCard;
